import { useQuery } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import {
  BellRing,
  CircleAlert,
  CircleCheckBig,
  FileCheck2,
  ListOrdered,
  MapPinPlus,
  Plus,
  Zap,
} from 'lucide-react'
import { myReluxesQuery, remindersQuery, staffQueueQuery } from '@/api/queries'
import { can, flag, isFieldUser, type Me } from '@/auth/me'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardTitle } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { FollowUpActions } from '@/features/notifications/follow-up-actions'
import { formatDay } from '@/lib/format'
import { LeagueSummary } from '@/features/league/league'

export const Route = createFileRoute('/_app/')({ component: Home })

function Home() {
  const { me } = Route.useRouteContext()
  return isFieldUser(me) ? <FieldHome me={me} /> : <StaffHome me={me} />
}

function greeting() {
  const h = new Date().getHours()
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'
}

const FOLLOW_UP_ACTIONS: Record<number, string[]> = {
  1: ['sent', 'not_yet'],
  2: ['quoted', 'still_waiting', 'lost'],
}
const FOLLOW_UP_TITLES: Record<number, string> = {
  1: 'Have you sent the design to the client?',
  2: 'Any feedback from the client?',
}

function FieldHome({ me }: { me: Me }) {
  const reluxes = useQuery(myReluxesQuery)
  const reminders = useQuery(remindersQuery)
  const infoNeeded = (reluxes.data ?? []).filter((r) => r.status === 'info_needed')
  const ready = (reluxes.data ?? []).filter(
    (r) => r.status === 'completed' && r.commercial_stage === 'design_issued',
  )
  const due = reminders.data ?? []
  const nothing = infoNeeded.length + ready.length + due.length === 0

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <h1 className="text-3xl uppercase">
        {greeting()}, {me.display_name}
      </h1>

      <Button asChild size="hero">
        <Link to="/reluxes/new">
          <Plus aria-hidden />
          New Relux
        </Link>
      </Button>

      <div className="grid gap-3 sm:grid-cols-2">
        {flag(me, 'quick_relux_mode') && (
          <Button asChild variant="outline" size="lg" block>
            <Link to="/reluxes/new" search={{ quick: true }}>
              <Zap aria-hidden />
              Quick Relux
            </Link>
          </Button>
        )}
        {flag(me, 'site_visits') && (
          <Button asChild variant="secondary" size="lg" block>
            <Link to="/visits/new">
              <MapPinPlus aria-hidden />
              Log Site Visit
            </Link>
          </Button>
        )}
      </div>

      <LeagueSummary />

      <section aria-labelledby="attention" className="flex flex-col gap-3">
        <h2 id="attention" className="text-xl uppercase">
          Needs your attention
        </h2>
        {nothing && (
          <EmptyState icon={CircleCheckBig} title="Nothing needs you right now">
            Designs that are ready, questions from the designer and follow-ups will show here.
          </EmptyState>
        )}
        {infoNeeded.map((r) => (
          <Link
            key={r.id}
            to="/reluxes/$reluxId"
            params={{ reluxId: r.id }}
            className="flex items-start gap-3 rounded-card border-2 border-danger bg-surface p-4 hover:bg-surface-2"
          >
            <CircleAlert className="mt-0.5 size-6 shrink-0 text-danger" aria-hidden />
            <span>
              <span className="block font-semibold">Designer needs info: {r.project_name}</span>
              {r.info_needed_message && (
                <span className="block text-fg-muted">{r.info_needed_message}</span>
              )}
            </span>
          </Link>
        ))}
        {ready.map((r) => (
          <Link
            key={r.id}
            to="/reluxes/$reluxId"
            params={{ reluxId: r.id }}
            className="flex items-start gap-3 rounded-card border-2 border-brand bg-surface p-4 hover:bg-surface-2"
          >
            <FileCheck2 className="mt-0.5 size-6 shrink-0 text-brand-text" aria-hidden />
            <span>
              <span className="block font-semibold">Your Relux for {r.project_name} is ready</span>
              <span className="block text-fg-muted">
                Share it with the client and record the outcome.
              </span>
            </span>
          </Link>
        ))}
        {due.map((rm) => (
          <Card key={rm.id} className="flex flex-col gap-3">
            <p className="flex items-start gap-3">
              <BellRing className="mt-0.5 size-6 shrink-0" aria-hidden />
              <span>
                <span className="block font-semibold">
                  {rm.kind === 'follow_up'
                    ? (FOLLOW_UP_TITLES[rm.step ?? 3] ?? 'Update the outcome')
                    : rm.kind === 'manual'
                      ? `Reminder from ${rm.from_name ?? 'Mo'}`
                      : rm.message}
                </span>
                <span className="block text-fg-muted">
                  {[rm.project_name, rm.kind === 'manual' && rm.message]
                    .filter(Boolean)
                    .join(' - ')}
                </span>
              </span>
            </p>
            {rm.kind === 'follow_up' && (
              <FollowUpActions
                reminderId={rm.id}
                reluxId={rm.relux_id}
                actions={
                  FOLLOW_UP_ACTIONS[rm.step ?? 3] ?? ['won', 'lost', 'still_pending', 'on_hold']
                }
              />
            )}
            {rm.kind === 'manual' && (
              <FollowUpActions reminderId={rm.id} reluxId={rm.relux_id} actions={['done']} />
            )}
          </Card>
        ))}
      </section>
    </div>
  )
}

function StaffHome({ me }: { me: Me }) {
  const queue = useQuery({ ...staffQueueQuery, enabled: can(me, 'projects.read_all') })
  const rows = queue.data ?? []
  const today = new Date().toISOString().slice(0, 10)
  const overdue = rows.filter((r) => r.eta && r.eta < today)
  const asked = rows.filter((r) => r.eta_requested_at && !r.eta)
  const urgent = rows.filter((r) => r.urgency)
  const next = rows.find((r) => r.status === 'submitted')

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-3xl uppercase">
        {greeting()}, {me.display_name}
      </h1>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="In the queue" value={rows.length} />
        <Stat label="In progress" value={rows.filter((r) => r.status === 'in_progress').length} />
        <Stat
          label="ETA overdue"
          value={overdue.length}
          tone={overdue.length ? 'danger' : undefined}
        />
        <Stat
          label="Asked for an ETA"
          value={asked.length}
          tone={asked.length ? 'warning' : undefined}
        />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="flex flex-col gap-3">
          <CardTitle>Next up</CardTitle>
          {next ? (
            <>
              <p className="text-lg font-semibold">{next.project_name}</p>
              <CardDescription>
                {next.ref} - {next.owner_name} - {next.application} - {next.completeness_score}%
                complete
              </CardDescription>
              <Button asChild className="self-start">
                <Link to="/queue" search={{ relux: next.id }}>
                  <ListOrdered aria-hidden /> Open
                </Link>
              </Button>
            </>
          ) : (
            <CardDescription>Nothing waiting to start.</CardDescription>
          )}
        </Card>
        <Card className="flex flex-col gap-3">
          <CardTitle>Flags</CardTitle>
          {urgent.length + overdue.length === 0 && (
            <CardDescription>No urgent or overdue Reluxes.</CardDescription>
          )}
          {urgent.map((r) => (
            <Link
              key={r.id}
              to="/queue"
              search={{ relux: r.id }}
              className="font-semibold text-danger underline underline-offset-4"
            >
              Urgent: {r.project_name} ({r.owner_name}) - {r.urgency_reason}
            </Link>
          ))}
          {overdue.map((r) => (
            <Link
              key={r.id}
              to="/queue"
              search={{ relux: r.id }}
              className="font-semibold underline underline-offset-4"
            >
              ETA {formatDay(r.eta)} passed: {r.project_name}
            </Link>
          ))}
        </Card>
      </div>
    </div>
  )
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone?: 'danger' | 'warning'
}) {
  return (
    <Card
      className={
        tone === 'danger'
          ? 'border-2 border-danger'
          : tone === 'warning'
            ? 'border-2 border-warning-fg'
            : undefined
      }
    >
      <p className="text-sm font-semibold text-fg-muted uppercase">{label}</p>
      <p className="font-display text-5xl">{value}</p>
    </Card>
  )
}
